import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Card, Checkbox, DatePicker, message, Popconfirm, Space, Spin } from "antd";
import { serverFetch } from "@extensions";
import "./history.css";

const { RangePicker } = DatePicker;

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

const emptyExtendedData = {
  accounts: [],
  deposits: [],
  certificates: [],
  abons: [],
};

const saleStatuses = {
  0: "Без статуса",
  1: "Новый",
  2: "Активный",
  3: "Заморожен",
  4: "Разморожен",
  5: "Закрыт",
};

const History = () => {
  const [extendedLoading, setExtendedLoading] = useState(false);
  const [extendedData, setExtendedData] = useState(emptyExtendedData);
  const [certificateLoadingId, setCertificateLoadingId] = useState(null);
  const [showAllCertificates, setShowAllCertificates] = useState(false);
  const [freezeRanges, setFreezeRanges] = useState({});
  const [freezingId, setFreezingId] = useState(null);

  const fetchExtendedData = useCallback(() => {
    setExtendedLoading(true);
    serverFetch("OnlineClient/ExtendedData", {
      method: "GET",
      queryParams: {
        includeClosedCertificates: showAllCertificates,
      },
    })
      .then((data) => {
        setExtendedData({
          accounts: Array.isArray(data?.accounts) ? data.accounts : [],
          deposits: Array.isArray(data?.deposits) ? data.deposits : [],
          certificates: Array.isArray(data?.certificates) ? data.certificates : [],
          abons: Array.isArray(data?.abons) ? data.abons : [],
        });
      })
      .catch((e) => {
        message.error(errMsg(e));
        setExtendedData(emptyExtendedData);
      })
      .finally(() => setExtendedLoading(false));
  }, [showAllCertificates]);

  useEffect(() => {
    fetchExtendedData();
  }, [fetchExtendedData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format("DD.MM.YYYY");
  };

  const formatPrice = (price) => {
    const value = Number(price || 0);
    return value.toLocaleString("ru-RU");
  };

  const formatQty = (qty) => {
    const value = Number(qty || 0);
    return value.toLocaleString("ru-RU");
  };

  const formatStatus = (status) => saleStatuses[status] || "Неизвестно";

  const formatMoney = (price) => `${formatPrice(price)} ₽`;

  const getStatusClass = (status) => {
    if (status === 2) return "history-status-active";
    if (status === 3) return "history-status-frozen";
    if (status === 5) return "history-status-closed";
    return "";
  };

  const handleCertificateView = (certificate) => {
    const certificateWindow = window.open("", "_blank");
    if (!certificateWindow) {
      message.error("Не удалось открыть окно печатной формы");
      return;
    }

    certificateWindow.document.write("<!doctype html><html><head><title>Сертификат</title></head><body>Загрузка...</body></html>");
    certificateWindow.document.close();

    setCertificateLoadingId(certificate.id);
    serverFetch(`OnlineClient/CertificatePrint/${certificate.id}`, { method: "GET" })
      .then((data) => {
        certificateWindow.document.open();
        certificateWindow.document.write(data?.html || "");
        certificateWindow.document.close();
        window.setTimeout(() => {
          certificateWindow.focus();
          certificateWindow.print();
        }, 300);
      })
      .catch((e) => {
        certificateWindow.close();
        message.error(errMsg(e));
      })
      .finally(() => setCertificateLoadingId(null));
  };

  const handleFreezeRangeChange = (saleId, value) => {
    setFreezeRanges((prev) => ({
      ...prev,
      [saleId]: value,
    }));
  };

  const handleFreeze = (sale) => {
    const range = freezeRanges[sale.id];
    if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) {
      message.warning("Укажите период заморозки");
      return;
    }

    setFreezingId(sale.id);
    serverFetch("OnlineClient/FreezeAbon", {
      method: "POST",
      bodyData: {
        saleId: sale.id,
        date: range[0].format("YYYY-MM-DD"),
        dateEnd: range[1].format("YYYY-MM-DD"),
        status: 3, // 3 - заморожен
      },
    })
      .then(() => {
        message.success("Абонемент заморожен");
        setFreezeRanges((prev) => ({
          ...prev,
          [sale.id]: null,
        }));
        fetchExtendedData();
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setFreezingId(null));
  };

  const renderBalanceCard = (item, type) => (
    <div key={`${type}-${item.id || item.name}`} className="history-wallet-balance">
      <div className="history-wallet-balance-icon">{type === "deposit" ? "%" : "₽"}</div>
      <div className="history-wallet-balance-sum">{formatMoney(item.syma)}</div>
      <div className="history-wallet-balance-name">{item.name || (type === "deposit" ? "Депозит" : "Счет")}</div>
    </div>
  );

  const renderWallet = () => {
    const accounts = extendedData.accounts || [];
    const deposits = extendedData.deposits || [];
    const rawBalances = [
      ...accounts.map((account) => ({ ...account, type: "account" })),
      ...deposits.map((deposit) => ({ ...deposit, type: "deposit" })),
    ];
    const seenBalances = new Set();
    const balances = rawBalances.filter((balance) => {
      const name = (balance.name || (balance.type === "deposit" ? "Депозит" : "Счет")).trim().toLowerCase();
      const key = `${name}-${Number(balance.syma || 0)}`;

      if (seenBalances.has(key)) return false;
      seenBalances.add(key);
      return true;
    });

    if (balances.length === 0) return null;

    return (
      <div className="history-wallet">
        <div className="history-section-head">
          <div className="history-section-title">Счета</div>
        </div>
        <div className="history-wallet-list">
          {balances.map((item) => renderBalanceCard(item, item.type))}
        </div>
      </div>
    );
  };

  const renderSection = (title, extra, children, className = "") => (
    <Card className={`history-panel ${className}`} bordered={false}>
      <div className="history-section-head">
        <div className="history-section-title">{title}</div>
        {extra}
      </div>
      <div className="history-list">{children}</div>
    </Card>
  );

  const renderCertificates = () => {
    const certificates = extendedData.certificates || [];
    if (certificates.length === 0 && showAllCertificates) return null;

    const filter = (
      <div className="history-section-action">
        <Checkbox
          checked={showAllCertificates}
          disabled={extendedLoading}
          className="history-certificate-filter"
          onChange={(e) => setShowAllCertificates(e.target.checked)}
        >
          Показать погашенные
        </Checkbox>
      </div>
    );

    return renderSection(
      "Сертификаты",
      filter,
      certificates.length > 0 ? (
        certificates.map((certificate) => (
          <div key={certificate.id} className="history-item">
            <div className="history-item-icon">→</div>
            <div className="history-item-body">
              <div className="history-item-name">{certificate.productName || `Сертификат №${certificate.id}`}</div>
              <div className="history-item-meta">
                <span>{formatDate(certificate.docDate)}</span>
                <span>№{certificate.id}</span>
                <span className={getStatusClass(certificate.status)}>{formatStatus(certificate.status)}</span>
                {certificate.finish && <span>до {formatDate(certificate.finish)}</span>}
              </div>
            </div>
            <div className="history-item-side">
              <div className="history-item-price">{formatMoney(certificate.syma)}</div>
              <Button
                size="small"
                className="history-item-button"
                loading={certificateLoadingId === certificate.id}
                onClick={() => handleCertificateView(certificate)}
              >
                Просмотр
              </Button>
            </div>
          </div>
        ))
      ) : (
        <div className="history-empty">Нет сертификатов</div>
      ),
      "history-certificates-panel",
    );
  };

  const renderAbons = () => {
    const abons = extendedData.abons || [];
    if (abons.length === 0) return null;

    return renderSection(
      "Абонементы",
      null,
      abons.map((abon) => {
        const canFreeze = abon.status === 2 && abon.canBeFrozen;
        const packageItems = Array.isArray(abon.packageItems) ? abon.packageItems : [];

        return (
          <div key={abon.id} className="history-item history-item-with-actions">
            <div className="history-item-icon">→</div>
            <div className="history-item-body">
              <div className="history-item-name">{abon.productName || `Продажа №${abon.id}`}</div>
              <div className="history-item-meta">
                <span>{formatDate(abon.docDate)}</span>
                <span className={getStatusClass(abon.status)}>{formatStatus(abon.status)}</span>
                {abon.finish && <span>до {formatDate(abon.finish)}</span>}
              </div>
              {packageItems.length > 0 && (
                <div className="history-package-rests">
                  {packageItems.map((item, index) => (
                    <div key={`${abon.id}-${item.productName}-${index}`} className="history-package-rest">
                      <span>{item.productName || "Пакет"}</span>
                      <span>
                        Остаток: {formatQty(item.qtyRest)} из {formatQty(item.qty)}
                        {item.expUnit ? ` ${item.expUnit}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="history-item-side">
              <div className="history-item-price">{formatMoney(abon.syma)}</div>
              {canFreeze && (
                <Space className="history-freeze-actions" wrap>
                  <RangePicker
                    value={freezeRanges[abon.id] || null}
                    format="DD.MM.YYYY"
                    onChange={(value) => handleFreezeRangeChange(abon.id, value)}
                  />
                  <Popconfirm
                    title="Заморозить?"
                    description="Заморозить абонемент на выбранный период?"
                    okText="Да"
                    cancelText="Нет"
                    onConfirm={() => handleFreeze(abon)}
                  >
                    <Button size="small" className="history-item-button" loading={freezingId === abon.id}>
                      Заморозить
                    </Button>
                  </Popconfirm>
                </Space>
              )}
            </div>
          </div>
        );
      }),
      "history-abons-panel",
    );
  };

  const renderExtendedData = () => {
    const hasExtendedData =
      (extendedData.accounts || []).length > 0 ||
      (extendedData.certificates || []).length > 0 ||
      (extendedData.abons || []).length > 0 ||
      (extendedData.deposits || []).length > 0 ||
      !showAllCertificates;

    if (extendedLoading) {
      return (
        <div className="history-loading">
          <Spin />
        </div>
      );
    }

    if (!hasExtendedData) return null;

    return (
      <div className="history-extended">
        {renderWallet()}
        {renderCertificates()}
        {renderAbons()}
      </div>
    );
  };

  return (
    <div className="history-context">
      {renderExtendedData()}
    </div>
  );
};

export default History;
